import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Line, Legend, BarChart, Bar, LineChart } from 'recharts';
import MainLayout from '../../components/MainLayout';
import InspectionPhotos from './machine/InspectionPhotos';
import OverviewNew from './machine/OverviewNew';
import ProcessNew from './machine/ProcessNew';
import DataLog from './machine/DataLog';
import WidgetDashboard from './machine/WidgetDashboard';
import { fetchMachineOverview, fetchEquipments, fetchFactories, fetchWidgetConfig, saveWidgetConfig, fetchMachineWidget, fetchCustomWidget } from '../../services/api';
import { getStoredUser } from '../../services/authService';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTranslation } from '../../context/LanguageContext';
import './Machine.css';

/* 헤더 브레드크럼의 "모니터링" 드롭다운 — Figma「개요」헤더(전사 › 모니터링 › 공장 › 사출기).
 * 사이드바의 모니터링 화면들로 바로 이동한다. 이름은 사이드바 번역 키를 그대로 쓴다. */
const MONITORING_PAGES = [
  { nameKey: 'sidebar.machine.dashboard', path: '/machine' },
  { nameKey: 'sidebar.machine.equipmentBoard', path: '/equipment-board' },
  { nameKey: 'sidebar.factory.overview', path: '/factory' },
  { nameKey: 'sidebar.factory.machineOverview', path: '/factory-machine-overview' },
  { nameKey: 'sidebar.factory.defectHistory', path: '/factory-defect-history' },
  { nameKey: 'sidebar.factory.realtimeShot', path: '/realtime-shot-status' }
];

/* 헤더 동기화 시각 표기 (YYYY-MM-DD HH:mm:ss). 공장 사출 현황 헤더와 같은 형식. */
const pad2 = (n) => String(n).padStart(2, '0');
const formatSyncTime = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '-';
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())} ${pad2(x.getHours())}:${pad2(x.getMinutes())}:${pad2(x.getSeconds())}`;
};

function Machine() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const machineData = location.state?.machineData;
  const openTab = location.state?.openTab; // 열어야 할 탭

  // machineData가 있으면 사용, 없으면 null (설비 목록에서 선택하도록)
  const machine = (machineData && machineData.id) ? machineData : null;

  // 사출기 목록 API 상태
  const [allMachines, setAllMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [selectedMachineId, setSelectedMachineId] = useState(machine?.id || null);

  // machineData가 변경되고 유효한 id가 있을 때만 업데이트
  useEffect(() => {
    if (machineData && machineData.id) {
      setSelectedMachineId(machineData.id);
    }
  }, [machineData]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFactoryDropdownOpen, setIsFactoryDropdownOpen] = useState(false);
  const [isMonitoringDropdownOpen, setIsMonitoringDropdownOpen] = useState(false);

  /* 브레드크럼 공장 링크용 공장 목록. 사출기 목록 응답에 factoryId 가 없는 경우
   * (백엔드 배포 전) 공장명으로 ID 를 찾아 올바른 공장 페이지로 보내기 위해 쓴다. */
  const [factoryList, setFactoryList] = useState([]);
  useEffect(() => {
    fetchFactories()
      .then((data) => setFactoryList(Array.isArray(data) ? data : []))
      .catch((err) => console.error('공장 목록 조회 실패:', err));
  }, []);

  const resolveFactoryId = (m) => {
    if (!m) return null;
    if (m.factoryId) return m.factoryId;
    const found = factoryList.find((f) => f.factoryName === m.factoryName);
    return found ? found.id : null;
  };
  const [activeTab, setActiveTab] = useState('대시보드');
  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);
  const [selectedWidgetSlot, setSelectedWidgetSlot] = useState(null);
  const [, setSelectedWidgetType] = useState(null);   // 값은 사용자 모듈 빌더 열 때만 쓰고 읽지는 않는다
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const dashboardWidgetsRef = useRef([]); // 최신 dashboardWidgets 참조
  const fetchAllWidgetDataRef = useRef(null); // fetchAllWidgetData 최신 참조
  const isFetchingWidgetDataRef = useRef(false); // 중복 fetch 방지 플래그
  const [showCustomWidgetBuilder, setShowCustomWidgetBuilder] = useState(false);
  const [customWidgetConfig, setCustomWidgetConfig] = useState({
    size: '1x1',
    title: '',
    chartType: 'line',
    xAxis: 'shotNumber',
    yAxis: 'pressure'
  });

  // Overview API 상태
  const [overviewApiData, setOverviewApiData] = useState(null);
  const [isOverviewLoading, setIsOverviewLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const refreshLabelRef = useRef(null);
  const refreshIconRef = useRef(null);

  // 현재 탭과 설비 ID를 ref로 저장 (useAutoRefresh 콜백에서 사용)
  const activeTabRef = useRef(activeTab);
  const selectedMachineIdRef = useRef(selectedMachineId);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    selectedMachineIdRef.current = selectedMachineId;
  }, [selectedMachineId]);

  useEffect(() => {
  }, []);

  // DOM 직접 조작으로 리프레시 표시 (리렌더링 방지)
  const setRefreshingUI = useCallback((isRefreshing) => {
    if (refreshLabelRef.current) {
      if (isRefreshing) {
        refreshLabelRef.current.classList.add('refreshing');
      } else {
        refreshLabelRef.current.classList.remove('refreshing');
      }
    }
    if (refreshIconRef.current) {
      if (isRefreshing) {
        refreshIconRef.current.classList.add('spinning');
      } else {
        refreshIconRef.current.classList.remove('spinning');
      }
    }
  }, []);

  // 사용자 정보 (로그인된 사용자) - useMemo로 캐싱하여 불필요한 리렌더링 방지
  const currentUser = React.useMemo(() => {
    const storedUser = getStoredUser();
    return storedUser ? { id: storedUser.id, name: storedUser.name } : null;
  }, []);

  // 위젯 데이터 API 상태
  const [widgetData, setWidgetData] = useState({});
  const widgetDataRef = useRef({}); // 최신 widgetData 참조용
  const [widgetLoading, setWidgetLoading] = useState({});
  const [, setIsAllWidgetsLoading] = useState(false); // 전체 위젯 로딩 상태(현재 읽는 곳 없음)

  // 위젯 설정 로드 상태
  const [isLoadingWidgetConfig, setIsLoadingWidgetConfig] = useState(false);

  // 외부 클릭 감지 - dropdown 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 모니터링·공장·사출기 드롭다운 모두 같은 래퍼 클래스를 쓴다. 바깥을 누르면 열린 쪽을 닫는다.
      const inDropdown = event.target.closest('.machine-dropdown-wrapper');
      if (!inDropdown) {
        if (isDropdownOpen) setIsDropdownOpen(false);
        if (isFactoryDropdownOpen) setIsFactoryDropdownOpen(false);
        if (isMonitoringDropdownOpen) setIsMonitoringDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isFactoryDropdownOpen, isMonitoringDropdownOpen]);

  // 사출기 목록 로딩
  useEffect(() => {
    setMachinesLoading(true);
    fetchEquipments()
      .then(equipments => {
        const equipmentList = equipments || [];
        setAllMachines(equipmentList);
        // machineData가 없고 설비 목록이 있으면 첫 번째 설비 선택
        if (!machine && equipmentList.length > 0) {
          setSelectedMachineId(equipmentList[0].id);
        }
      })
      .catch(error => {
        console.error('Failed to load equipments:', error);
      })
      .finally(() => {
        setMachinesLoading(false);
      });
  }, [machine]);

  /* 탭 이동. 새 디자인 탭은 서브탭을 가지므로(공정: cycle/temperature/drive/monitor/setup, 검수사진: spc/judge/production/photos,
   * 데이터로그: shots/drive/alarms/analysis) 요청한 서브탭을 기억해 두었다가 initialSubTab 으로 넘긴다.
   * 탭 컴포넌트는 탭 전환 시 다시 마운트되므로 initialSubTab 만으로 충분하다. */
  const [subTabRequest, setSubTabRequest] = useState({});
  const navigateTab = useCallback((tab, sub) => {
    if (sub) setSubTabRequest((prev) => ({ ...prev, [tab]: sub }));
    setActiveTab(tab);
  }, []);

  // 다른 화면에서 전달된 openTab 파라미터 처리
  //   alarm → 데이터·로그 > 알람 이력 / fusion·vision·judge → 품질·생산 > 융합검사·비전검사·공정검사 (공장 > 불량 이력에서 사용)
  useEffect(() => {
    if (openTab === 'alarm') {
      navigateTab('데이터로그', 'alarms');
    } else if (openTab === 'fusion' || openTab === 'vision' || openTab === 'judge') {
      navigateTab('검수사진', openTab === 'vision' ? 'photos' : openTab);
    }
  }, [openTab, navigateTab]);

  const currentMachine = allMachines.find(m => m.id === selectedMachineId) || allMachines[0] || null;

  /* ---- 헤더 공장 토글 ----
   * 설비 목록은 계정이 볼 수 있는 전 공장 설비가 한 배열로 오므로, 공장별로 묶어 토글로 고르고
   * 사출기 드롭다운에는 고른 공장의 설비만 보인다. 현재 설비의 공장이 곧 활성 공장이라
   * 별도 상태 없이 파생값으로 둔다(알람 이동 등으로 설비가 바뀌면 토글도 따라간다). */
  const factoryKeyOf = (m) => {
    if (!m) return null;
    const id = m.factoryId ?? resolveFactoryId(m);
    return id != null ? `id:${id}` : (m.factoryName ? `name:${m.factoryName}` : null);
  };
  const factoryOptions = [];
  allMachines.forEach((m) => {
    const key = factoryKeyOf(m);
    if (!key) return;
    let opt = factoryOptions.find((o) => o.key === key);
    if (!opt) {
      opt = { key, name: m.factoryName || t('pages.monitoring.machine.selectFactory'), machines: [] };
      factoryOptions.push(opt);
    }
    opt.machines.push(m);
  });
  // 공장 목록 API 순서(있으면)로 정렬해 화면마다 순서가 같게 한다
  factoryOptions.sort((a, b) => {
    const ia = factoryList.findIndex((f) => `id:${f.id}` === a.key);
    const ib = factoryList.findIndex((f) => `id:${f.id}` === b.key);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });
  const activeFactoryKey = factoryKeyOf(currentMachine);
  const visibleMachines = activeFactoryKey
    ? allMachines.filter((m) => factoryKeyOf(m) === activeFactoryKey)
    : allMachines;
  const selectFactory = (opt) => {
    if (!opt || opt.key === activeFactoryKey || opt.machines.length === 0) return;
    const first = opt.machines[0];
    setSelectedMachineId(first.id);
    setIsDropdownOpen(false);
  };

  // Overview 데이터 초기 로딩 (모든 탭에서 기본 데이터로 필요)
  useEffect(() => {
    const equipmentId = selectedMachineId || machine?.id;

    if (equipmentId) {
      setIsOverviewLoading(true);
      fetchMachineOverview(equipmentId)
        .then(data => {
          if (data) {
            setOverviewApiData(data);
            setLastUpdated(new Date());
          }
        })
        .catch(error => {
          console.error('Failed to load overview data:', error);
        })
        .finally(() => {
          setIsOverviewLoading(false);
        });
    }
  }, [selectedMachineId, machine?.id]);

  // 위젯 설정 로드 (페이지 로드 시 & 장비 변경 시)
  useEffect(() => {
    const loadWidgetConfig = async () => {
      if (!currentUser?.id) {
        return;
      }

      setIsLoadingWidgetConfig(true);

      try {
        const data = await fetchWidgetConfig(currentUser.id);

        if (data && data.length > 0) {
          // 저장된 위젯 설정을 dashboardWidgets 상태로 변환
          const widgets = data.map((config, index) => ({
            id: config.id,  // 데이터베이스 ID (사용자모듈 API 호출에 필요)
            type: config.widgetType,
            size: config.widgetSize,
            slot: config.slotNumber,
            config: config.customConfig
          }));
          setDashboardWidgets(widgets);
          // dashboardWidgets 변경으로 자동으로 데이터 fetch가 실행됨
        } else {
          // 저장된 설정이 없으면 빈 배열
          setDashboardWidgets([]);
        }
      } catch (error) {
        console.error('Failed to load widget config:', error);
      } finally {
        setIsLoadingWidgetConfig(false);
      }
    };

    loadWidgetConfig();
  }, [currentUser]);

  // 공정 > 설정값 탭의 "대시보드 위젯으로 추가": 기본사출정보 위젯을 첫 빈 슬롯(1x1)에 넣고 대시보드 탭으로 이동.
  // 이미 있으면 추가하지 않고 이동만 한다. 저장은 아래 자동 저장 effect 가 처리한다.
  const addInjectionInfoWidget = () => {
    if (!dashboardWidgets.some((w) => w.type === '기본사출정보')) {
      const occupied = new Set();
      dashboardWidgets.forEach((w) => {
        if (!w.slot) return;
        occupied.add(w.slot);
        if (w.size === '2x1' || w.size === '2x2') occupied.add(w.slot + 1);
        if (w.size === '1x2' || w.size === '2x2') occupied.add(w.slot + 4);
        if (w.size === '2x2') occupied.add(w.slot + 5);
      });
      let free = null;
      for (let s = 1; s <= 12 && free === null; s += 1) {
        if (!occupied.has(s)) free = s;
      }
      if (free === null) {
        alert(t('pages.monitoring.machine.processNew.setupWidgetNoSlot'));
        return;
      }
      setDashboardWidgets([...dashboardWidgets, { id: Date.now(), type: '기본사출정보', size: '1x1', slot: free }]);
    }
    setActiveTab('대시보드');
  };

  // 위젯 설정 저장 (위젯 변경 시 자동 저장 - 1초 debounce)
  useEffect(() => {
    // 로딩 중이거나 필수 정보가 없으면 저장 안 함
    if (!currentUser?.id || isLoadingWidgetConfig) {
      return;
    }

    const saveWidgetSettings = async () => {
      try {
        const result = await saveWidgetConfig(currentUser.id, dashboardWidgets);

        if (result) {
          // ID가 변경된 위젯이 있는지 확인
          const hasIdChanged = result.some((savedConfig) => {
            const existingWidget = dashboardWidgets.find(w => w.slot === savedConfig.slotNumber);
            return existingWidget && existingWidget.id !== savedConfig.id;
          });

          // ID가 변경된 경우에만 상태 업데이트
          if (hasIdChanged) {
            // 서버에서 반환한 savedConfigs로 위젯 상태 업데이트
            const updatedWidgets = result.map((savedConfig) => {
              // 기존 위젯에서 slot이 같은 것을 찾아 병합
              const existingWidget = dashboardWidgets.find(w => w.slot === savedConfig.slotNumber);
              return {
                id: savedConfig.id,  // 데이터베이스 ID
                type: savedConfig.widgetType,
                size: savedConfig.widgetSize,
                slot: savedConfig.slotNumber,
                config: savedConfig.customConfig || existingWidget?.config,
                _oldId: existingWidget?.id  // 기존 ID 저장 (widgetData 매핑용)
              };
            });

            // 🔧 widgetData의 키도 새 ID로 매핑
            setWidgetData(prevData => {
              const newData = { ...prevData };
              updatedWidgets.forEach(widget => {
                if (widget.type === '사용자모듈' && widget._oldId && widget._oldId !== widget.id) {
                  const oldKey = `사용자모듈_${widget._oldId}`;
                  const newKey = `사용자모듈_${widget.id}`;
                  if (newData[oldKey] !== undefined) {
                    newData[newKey] = newData[oldKey];
                    delete newData[oldKey];
                  }
                }
              });
              return newData;
            });

            // 로딩 플래그를 일시적으로 true로 설정하여 무한 루프 방지
            setIsLoadingWidgetConfig(true);
            setDashboardWidgets(updatedWidgets.map(({ _oldId, ...widget }) => widget)); // _oldId 제거

            // 다음 tick에서 로딩 플래그 해제
            setTimeout(() => {
              setIsLoadingWidgetConfig(false);
            }, 100);
            // dashboardWidgets 변경으로 자동으로 데이터 fetch가 실행됨
          }
        }
      } catch (error) {
        console.error('Failed to save widget config:', error);
      }
    };

    // 위젯 변경 후 1초 뒤 자동 저장 (debounce)
    const timer = setTimeout(saveWidgetSettings, 1000);
    return () => clearTimeout(timer);
  }, [dashboardWidgets, selectedMachineId, machine?.id, currentUser]);

  // dashboardWidgets를 ref에 동기화
  useEffect(() => {
    dashboardWidgetsRef.current = dashboardWidgets;
  }, [dashboardWidgets]);

  // 위젯 데이터 fetch 함수 (공통) - useCallback으로 정의하여 즉시 사용 가능
  const fetchAllWidgetData = useCallback(async () => {
    const equipmentId = selectedMachineId || machine?.id;
    const widgets = dashboardWidgetsRef.current; // ref에서 최신 값 참조

    // 이미 fetch 중이면 중복 실행 방지
    if (isFetchingWidgetDataRef.current) {
      return;
    }

    if (!equipmentId || widgets.length === 0) {
      return;
    }

    // 중복 실행 방지 플래그 설정
    isFetchingWidgetDataRef.current = true;

    // 전체 로딩 시작
    setIsAllWidgetsLoading(true);

    // 모든 데이터를 임시로 저장할 객체
    const newWidgetData = {};
    const newWidgetLoading = {};

    // 기본 위젯(온도·압력·에너지·기본정보·알람이력·기본사출정보·사이클·SPC·양불)은 machine/DashboardWidgets 가 직접 불러온다.
    // 여기서는 사용자모듈만 다루므로 기본 위젯 목록은 비운다(이중 호출 방지).
    const basicWidgets = [];
    const uniqueTypes = [...new Set(basicWidgets.map(w => w.type))];

    // 사용자모듈 위젯도 machine/DashboardWidgets 가 직접 불러온다 (여기 루프는 비워 둔다)
    const customWidgets = widgets.filter(() => false);

    // 초기 로드 시에만 로딩 상태 설정 (데이터가 없는 위젯만)
    // 이미 데이터가 있는 경우에는 로딩 표시 없이 백그라운드에서 업데이트
    const currentData = widgetDataRef.current;
    setWidgetLoading(prev => {
      const updated = { ...prev };
      uniqueTypes.forEach(type => {
        // 이미 데이터가 있으면 로딩 상태 변경하지 않음
        if (!currentData[type]) {
          updated[type] = true;
        }
      });
      customWidgets.forEach(widget => {
        if (widget.id) {
          const customKey = `사용자모듈_${widget.id}`;
          if (!currentData[customKey]) {
            updated[customKey] = true;
          }
        }
      });
      return updated;
    });

    try {
      // 기본 위젯 데이터 fetch (Promise.all로 병렬 처리)
      const basicFetchPromises = uniqueTypes.map(async (type) => {

        try {
          const data = await fetchMachineWidget(equipmentId, type);

          if (data) {
            // 알람이력 위젯은 전체 data 객체를 저장 (alarmHistory 배열 포함)
            if (type === '알람이력') {
              newWidgetData[type] = data;
            }
            // 기본정보 위젯은 basicInfo 객체를 저장
            else if (type === '기본정보') {
              newWidgetData[type] = data.basicInfo;
            }
            // 기본사출정보 위젯은 전체 data 객체를 저장 (injectionInfo 객체 포함)
            else if (type === '기본사출정보') {
              newWidgetData[type] = data;
            }
            else if (data.data) {
              newWidgetData[type] = data.data;
            }
          }
        } catch (error) {
          console.error(`Failed to fetch ${type} widget data:`, error);
        } finally {
          newWidgetLoading[type] = false;
        }
      });

      // 사용자모듈 위젯: 각각 개별적으로 fetch (Promise.all로 병렬 처리)
      const customFetchPromises = customWidgets.map(async (widget) => {
        if (!widget.id) {
          console.error('사용자모듈 위젯에 ID가 없습니다:', widget);
          return;
        }

        const customKey = `사용자모듈_${widget.id}`;

        try {
          const data = await fetchCustomWidget(equipmentId, widget.id);

          if (data && data.data) {
            newWidgetData[customKey] = data.data;
          }
        } catch (error) {
          console.error(`Failed to fetch custom widget ${widget.id} data:`, error);
        } finally {
          newWidgetLoading[customKey] = false;
        }
      });

      // 모든 fetch가 완료될 때까지 대기
      await Promise.all([...basicFetchPromises, ...customFetchPromises]);

      // 모든 데이터를 한 번에 업데이트 (기존 데이터와 병합)
      setWidgetData(prev => ({ ...prev, ...newWidgetData }));
      setWidgetLoading(prev => ({ ...prev, ...newWidgetLoading }));
    } finally {
      // 전체 로딩 종료 및 플래그 해제
      setIsAllWidgetsLoading(false);
      isFetchingWidgetDataRef.current = false;
    }
  }, [selectedMachineId, machine?.id]);

  // fetchAllWidgetData를 ref에 저장 (interval에서 최신 함수 참조용)
  useEffect(() => {
    fetchAllWidgetDataRef.current = fetchAllWidgetData;
  }, [fetchAllWidgetData]);

  // widgetData가 변경될 때마다 ref 업데이트
  useEffect(() => {
    widgetDataRef.current = widgetData;
  }, [widgetData]);

  // 대시보드 탭 진입 시 데이터 fetch
  useEffect(() => {
    if (activeTab === '대시보드' && dashboardWidgets.length > 0 && !isLoadingWidgetConfig) {
      // 탭 진입 시 항상 갱신
      if (fetchAllWidgetDataRef.current) {
        fetchAllWidgetDataRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isLoadingWidgetConfig]);

  // 통합 자동 갱신 콜백 함수
  const handleAutoRefresh = useCallback(async () => {
    const currentTab = activeTabRef.current;
    const equipmentId = selectedMachineIdRef.current || machine?.id;

    setRefreshingUI(true);

    try {
      switch (currentTab) {
        case '대시보드':
          // 위젯 데이터와 Overview 데이터(lastSyncTime 포함) 동시 갱신
          const overviewPromise = equipmentId ? fetchMachineOverview(equipmentId) : Promise.resolve(null);
          const widgetPromise = fetchAllWidgetDataRef.current ? fetchAllWidgetDataRef.current() : Promise.resolve();
          const [overviewData] = await Promise.all([overviewPromise, widgetPromise]);
          if (overviewData) {
            setOverviewApiData(overviewData);
          }
          break;
        default:
          break;
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error('[자동 갱신] 실패:', error);
    }

    setTimeout(() => {
      setRefreshingUI(false);
    }, 1500);
  }, [machine?.id, setRefreshingUI]);

  // useAutoRefresh 훅 사용 (30초 쿨다운 적용)
  useAutoRefresh(handleAutoRefresh);

  const tabs = [
    { name: '대시보드', labelKey: 'pages.monitoring.machine.tabs.dashboard', icon: 'dashboard', color: '#60A5FA' },
    { name: '개요', labelKey: 'pages.monitoring.machine.tabs.overviewNew', icon: 'space_dashboard', color: '#38BDF8' },
    { name: '공정', labelKey: 'pages.monitoring.machine.tabs.process', icon: 'precision_manufacturing', color: '#F472B6' },
    /* 품질 · 생산 — 새 디자인 탭. 내부 이름은 기존 '검수사진' 을 유지한다(서브탭: 공정관리 SPC / 공정검사 / 비전검사 / 생산) */
    { name: '검수사진', labelKey: 'pages.monitoring.machine.tabs.quality', icon: 'fact_check', color: '#3B82F6' },
    /* 데이터 · 로그 — 새 디자인 탭 (서브탭: 샷로그 / 구동부 작동이력 / 알람 이력 / 알람 분석). 구현은 ./machine/DataLog 에 분리 */
    { name: '데이터로그', labelKey: 'pages.monitoring.machine.tabs.dataLog', icon: 'database', color: '#14B8A6' }
  ];

  // 커스텀 위젯 미리보기 렌더링
  const renderCustomPreview = () => {
    const previewData = Array.from({ length: 20 }, (_, i) => ({
      shotNumber: i + 1,
      pressure: 80 + Math.random() * 20,
      temperature: 230 + Math.random() * 20,
      speed: 50 + Math.random() * 30,
      position: 100 + Math.random() * 50
    }));

    if (customWidgetConfig.chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey={customWidgetConfig.xAxis} stroke="#9CA3AF" fontSize={10} />
            <YAxis stroke="#9CA3AF" fontSize={10} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
              labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
            />
            <Line type="monotone" dataKey={customWidgetConfig.yAxis} stroke="#3B82F6" strokeWidth={1} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    } else if (customWidgetConfig.chartType === 'bar') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey={customWidgetConfig.xAxis} stroke="#9CA3AF" fontSize={10} />
            <YAxis stroke="#9CA3AF" fontSize={10} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
              labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
            />
            <Bar dataKey={customWidgetConfig.yAxis} fill="#8B5CF6" />
          </BarChart>
        </ResponsiveContainer>
      );
    } else if (customWidgetConfig.chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={previewData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey={customWidgetConfig.xAxis} stroke="#9CA3AF" fontSize={10} />
            <YAxis stroke="#9CA3AF" fontSize={10} />
            <Tooltip
              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
              labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
            />
            <Area type="monotone" dataKey={customWidgetConfig.yAxis} stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
  };

  // 위젯 렌더링 함수
  const renderWidget = (widget) => {
    // 기본 위젯용 데이터 (사용자모듈 제외)
    const data = widgetData[widget.type] || [];
    const loading = widgetLoading[widget.type];

    // 사용자모듈은 별도 키로 로딩 상태 관리하므로 case 내부에서 처리
    if (widget.type !== '사용자모듈') {
      // 로딩 중
      if (loading) {
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#9CA3AF'
          }}>
            {t('pages.monitoring.machine.common.loading')}
          </div>
        );
      }
    }

    // 데이터 없음 (사용자모듈과 생산 위젯은 제외)
    if (data.length === 0 && widget.type !== '사용자모듈' && widget.type !== '생산') {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#9CA3AF'
        }}>
          {t('pages.monitoring.machine.common.noData')}
        </div>
      );
    }

    switch(widget.type) {
      case '온도':
        // Y축 범위 계산 (모든 zone 값 고려)
        const tempValues = data.flatMap(d => [d.zone1, d.zone2, d.zone3, d.zone4, d.zone5].filter(v => v != null));
        const tempMin = tempValues.length > 0 ? Math.min(...tempValues) : 0;
        const tempMax = tempValues.length > 0 ? Math.max(...tempValues) : 100;
        const tempRange = tempMax - tempMin;
        const tempPadding = tempRange * 0.1; // 10% 여유

        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={[...data].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shotNumber"
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                domain={[tempMin - tempPadding, tempMax + tempPadding]}
                tickFormatter={(value) => value.toFixed(1)}
                label={{ value: t('pages.monitoring.machine.chartLabel.tempC'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
              />
              <Legend
                wrapperStyle={{
                  position: 'absolute',
                  top: '5px',
                  right: '30px',
                  fontSize: '10px',
                  width: 'auto'
                }}
                iconType="line"
                iconSize={16}
                layout="horizontal"
              />
              <Line type="monotone" dataKey="zone1" stroke="#EF4444" strokeWidth={1} dot={false} name="Zone 1" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone2" stroke="#F59E0B" strokeWidth={1} dot={false} name="Zone 2" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone3" stroke="#22C55E" strokeWidth={1} dot={false} name="Zone 3" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone4" stroke="#3B82F6" strokeWidth={1} dot={false} name="Zone 4" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="zone5" stroke="#8B5CF6" strokeWidth={1} dot={false} name="Zone 5" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
            </LineChart>
          </ResponsiveContainer>
        );
      case '압력':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={[...data].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shotNumber"
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: t('pages.monitoring.machine.chartLabel.pressureMpa'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
              />
              <Legend
                wrapperStyle={{
                  position: 'absolute',
                  top: '5px',
                  right: '30px',
                  fontSize: '10px',
                  width: 'auto'
                }}
                iconType="line"
                iconSize={16}
                layout="horizontal"
              />
              <Line type="monotone" dataKey="injection" stroke="#3B82F6" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.injectionPressure')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="holding" stroke="#8B5CF6" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.holding')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="metering" stroke="#10B981" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.meteringPressure')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              <Line type="monotone" dataKey="backPressure" stroke="#F59E0B" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.backPressure')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
            </LineChart>
          </ResponsiveContainer>
        );
      case '생산':
        return (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#9CA3AF'
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: '48px', marginBottom: '10px' }}>
              construction
            </span>
            <div>{t('pages.monitoring.machine.common.productionWidgetWip')}</div>
          </div>
        );
      case '에너지':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={[...data].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis
                dataKey="shotNumber"
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
              />
              <YAxis
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                label={{ value: t('pages.monitoring.machine.chartLabel.powerKw'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
              />
              <Legend
                wrapperStyle={{
                  position: 'absolute',
                  top: '5px',
                  right: '30px',
                  fontSize: '10px',
                  width: 'auto'
                }}
                iconType="line"
                iconSize={16}
                layout="horizontal"
              />
              <Area type="monotone" dataKey="power" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} name={t('pages.monitoring.machine.legendName.pressureKw')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
            </AreaChart>
          </ResponsiveContainer>
        );
      case '기본정보':
        const basicInfo = widgetData['기본정보'] || {};
        return (
          <div className="info-list" style={{
            height: '100%',
            padding: '8px'
          }}>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.productName')}</span>
              <span className="info-value">{basicInfo.productName || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.shotCount')}</span>
              <span className="info-value">
                {basicInfo.shotCount !== undefined ? basicInfo.shotCount.toLocaleString() : '-'}
                <span className="info-unit"> {t('pages.monitoring.machine.overview.labels.unitCount')}</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.cycleTime')}</span>
              <span className="info-value">
                {basicInfo.cycleTime !== undefined ? basicInfo.cycleTime.toFixed(1) : '-'}
                <span className="info-unit"> s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.cavityCount')}</span>
              <span className="info-value">
                {basicInfo.cavityCount !== undefined ? basicInfo.cavityCount : '-'}
                <span className="info-unit"> {t('pages.monitoring.machine.overview.labels.unitPiece')}</span>
              </span>
            </div>
          </div>
        );

      case '알람이력':
        const alarmHistory = widgetData['알람이력']?.alarmHistory || [];

        if (alarmHistory.length === 0) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              {t('pages.monitoring.machine.common.alarmHistoryEmpty')}
            </div>
          );
        }

        // 심각도별 스타일 (CRITICAL, WARNING, INFO)
        const getSeverityStyleAlarm = (severity) => {
          switch (severity) {
            case 'CRITICAL':
              return {
                bg: 'rgba(239, 68, 68, 0.2)',
                border: 'rgba(239, 68, 68, 0.3)',
                text: '#f87171',
                icon: 'error'
              };
            case 'WARNING':
              return {
                bg: 'rgba(251, 191, 36, 0.2)',
                border: 'rgba(251, 191, 36, 0.3)',
                text: '#fbbf24',
                icon: 'warning'
              };
            case 'INFO':
              return {
                bg: 'rgba(59, 130, 246, 0.2)',
                border: 'rgba(59, 130, 246, 0.3)',
                text: '#60a5fa',
                icon: 'info'
              };
            default:
              return {
                bg: 'rgba(107, 114, 128, 0.2)',
                border: 'rgba(107, 114, 128, 0.3)',
                text: '#9ca3af',
                icon: 'circle'
              };
          }
        };

        // 시간 포맷팅
        const formatAlarmTime = (timeStr) => {
          const date = new Date(timeStr);
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${month}/${day} ${hours}:${minutes}:${seconds}`;
        };

        return (
          <div style={{
            height: '100%',
            overflow: 'auto',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
          className="hide-scrollbar"
          >
            {alarmHistory.map((alarm, index) => {
              const severityStyle = getSeverityStyleAlarm(alarm.severity);
              return (
                <div
                  key={index}
                  style={{
                    display: 'block',
                    padding: '8px',
                    border: `1px solid ${severityStyle.border}`,
                    backgroundColor: severityStyle.bg,
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                  onClick={() => navigate(`/alarm-analysis?machine=${encodeURIComponent(currentMachine?.name || machine.name)}&alarmId=${alarm.id || index}`)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1,
                      minWidth: 0
                    }}>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: severityStyle.text,
                        flexShrink: 0
                      }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          {severityStyle.icon}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{
                            color: '#fff',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            #{alarm.code}
                          </span>
                          {alarm.value && (
                            <span style={{
                              color: '#9ca3af',
                              fontSize: '11px'
                            }}>
                              {alarm.value}
                            </span>
                          )}
                          {alarm.status === 'active' && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <div style={{
                                width: '6px',
                                height: '6px',
                                background: '#ef4444',
                                borderRadius: '50%',
                                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                              }}></div>
                              <span style={{
                                color: '#f87171',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                LIVE
                              </span>
                            </div>
                          )}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: severityStyle.text
                        }}>
                          {alarm.message}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    color: '#9ca3af',
                    marginTop: '4px',
                    marginLeft: '28px'
                  }}>
                    <span>{t('pages.monitoring.machine.common.occurredPrefix')}{formatAlarmTime(alarm.time)}</span>
                    <span style={{
                      color: alarm.status === 'active' ? '#f87171' : '#4ade80',
                      fontWeight: '500'
                    }}>
                      {alarm.status === 'active' ? t('pages.monitoring.machine.common.occurring') : t('pages.monitoring.machine.common.resolved')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case '기본사출정보':
        const injectionInfo = widgetData['기본사출정보']?.injectionInfo;

        if (!injectionInfo) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              {t('pages.monitoring.machine.common.noData')}
            </div>
          );
        }

        return (
          <div className="info-list" style={{
            height: '100%',
            padding: '8px',
            gridTemplateRows: 'repeat(4, 1fr)'
          }}>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.maxPressure')}</span>
              <span className="info-value">
                {injectionInfo.maxPressure !== null && injectionInfo.maxPressure !== undefined
                  ? injectionInfo.maxPressure.toFixed(1)
                  : '-'}
                <span className="info-unit"> MPa</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.startPosition')}</span>
              <span className="info-value">
                {injectionInfo.startPosition !== null && injectionInfo.startPosition !== undefined
                  ? injectionInfo.startPosition.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.switchPosition')}</span>
              <span className="info-value">
                {injectionInfo.switchPosition !== null && injectionInfo.switchPosition !== undefined
                  ? injectionInfo.switchPosition.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.injectionTime')}</span>
              <span className="info-value">
                {injectionInfo.time !== null && injectionInfo.time !== undefined
                  ? injectionInfo.time.toFixed(1)
                  : '-'}
                <span className="info-unit"> s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.injectionSpeed')}</span>
              <span className="info-value">
                {injectionInfo.speed !== null && injectionInfo.speed !== undefined
                  ? injectionInfo.speed.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm/s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.suckbackTime')}</span>
              <span className="info-value">
                {injectionInfo.suckbackTime !== null && injectionInfo.suckbackTime !== undefined
                  ? injectionInfo.suckbackTime.toFixed(1)
                  : '-'}
                <span className="info-unit"> s</span>
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('pages.monitoring.machine.overview.labels.suckbackSpeed')}</span>
              <span className="info-value">
                {injectionInfo.suckbackSpeed !== null && injectionInfo.suckbackSpeed !== undefined
                  ? injectionInfo.suckbackSpeed.toFixed(1)
                  : '-'}
                <span className="info-unit"> mm/s</span>
              </span>
            </div>
          </div>
        );

      case '사용자모듈':
        // 사용자 정의 위젯 렌더링
        const customKey = `사용자모듈_${widget.id}`;
        const customData = widgetData[customKey] || [];
        const customLoading = widgetLoading[customKey];
        // 이 위젯의 데이터가 한번이라도 fetch되었는지 확인
        const hasCustomDataLoaded = widgetData.hasOwnProperty(customKey);

        if (!widget.config) {
          console.error('사용자모듈 위젯 설정 오류:', widget);
          return <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '20px' }}>{t('pages.monitoring.machine.common.configError')}</div>;
        }

        // 아직 데이터가 한번도 로드되지 않은 경우 (초기 상태)
        // → 로딩 중 또는 빈 상태 표시 (데이터 없음이 아님)
        if (!hasCustomDataLoaded) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              {customLoading ? t('pages.monitoring.machine.common.loading') : ''}
            </div>
          );
        }

        // 데이터 로드 후 빈 배열인 경우
        if (customData.length === 0) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#9CA3AF'
            }}>
              {t('pages.monitoring.machine.common.noData')}
            </div>
          );
        }

        // ⭐ 압력 차트인 경우: 3가지 압력 모두 표시
        if (widget.config.yAxis === 'pressure') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                  interval={1}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.pressureMpa'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="injection" stroke="#3B82F6" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.injectionPressure')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="holding" stroke="#8B5CF6" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.holding')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="metering" stroke="#10B981" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.meteringPressure')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="backPressure" stroke="#F59E0B" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.backPressure')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        // ⭐ 온도 차트인 경우: 5개 zone 모두 표시
        if (widget.config.yAxis === 'temperature') {
          // 온도 데이터의 최소/최대값 계산
          const tempValues = customData.flatMap(item => [
            item.zone1, item.zone2, item.zone3, item.zone4, item.zone5
          ].filter(val => val !== undefined && val !== null));

          const minTemp = tempValues.length > 0 ? Math.min(...tempValues) : 0;
          const maxTemp = tempValues.length > 0 ? Math.max(...tempValues) : 300;

          // 여유 범위 추가 (최소값에서 -10, 최대값에서 +10)
          const yMin = Math.max(0, Math.floor(minTemp / 10) * 10 - 10);
          const yMax = Math.ceil(maxTemp / 10) * 10 + 10;

          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                />
                <YAxis
                  domain={[yMin, yMax]}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.tempC'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="zone1" stroke="#EF4444" strokeWidth={1} dot={false} name="Zone 1" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone2" stroke="#F59E0B" strokeWidth={1} dot={false} name="Zone 2" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone3" stroke="#22C55E" strokeWidth={1} dot={false} name="Zone 3" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone4" stroke="#3B82F6" strokeWidth={1} dot={false} name="Zone 4" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
                <Line type="monotone" dataKey="zone5" stroke="#8B5CF6" strokeWidth={1} dot={false} name="Zone 5" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        // ⭐ 속도 차트인 경우
        if (widget.config.yAxis === 'speed') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.speedMms'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="speed" stroke="#10B981" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.speed')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        // ⭐ 위치 차트인 경우
        if (widget.config.yAxis === 'position') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()} margin={{ top: 5, right: -20, left: -20, bottom: -5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey={widget.config.xAxis || 'shotNumber'}
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.shotNumber'), position: 'insideBottom', offset: -5, fill: '#9CA3AF', fontSize: 11 }}
                />
                <YAxis
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 10 }}
                  label={{ value: t('pages.monitoring.machine.chartLabel.positionMm'), angle: -90, position: 'center', style: { textAnchor: 'middle' }, fill: '#9CA3AF', fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Legend
                  wrapperStyle={{
                    position: 'absolute',
                    top: '5px',
                    right: '30px',
                    fontSize: '10px',
                    width: 'auto'
                  }}
                  iconType="line"
                  iconSize={16}
                  layout="horizontal"
                />
                <Line type="monotone" dataKey="position" stroke="#F59E0B" strokeWidth={1} dot={false} name={t('pages.monitoring.machine.legendName.position')} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        }

        if (widget.config.chartType === 'line') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...customData].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey={widget.config.xAxis} stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Line type="monotone" dataKey={widget.config.yAxis} stroke="#3B82F6" strokeWidth={1} dot={false} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </LineChart>
            </ResponsiveContainer>
          );
        } else if (widget.config.chartType === 'bar') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[...customData].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey={widget.config.xAxis} stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Bar dataKey={widget.config.yAxis} fill="#8B5CF6" isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </BarChart>
            </ResponsiveContainer>
          );
        } else if (widget.config.chartType === 'area') {
          return (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={[...customData].reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey={widget.config.xAxis} stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', fontSize: '12px' }}
                  labelStyle={{ color: '#E5E7EB', fontSize: '13px' }}
                />
                <Area type="monotone" dataKey={widget.config.yAxis} stroke="#10B981" fill="#10B981" fillOpacity={0.3} isAnimationActive={true} animationDuration={300} animationEasing="ease-out" />
              </AreaChart>
            </ResponsiveContainer>
          );
        }
        return <div style={{ color: '#9CA3AF', fontSize: '14px' }}>{t('pages.monitoring.machine.common.widgetContent')}</div>;
      default:
        return <div style={{ color: '#9CA3AF', fontSize: '14px' }}>{t('pages.monitoring.machine.common.widgetContent')}</div>;
    }
  };

  // 로딩 중
  if (machinesLoading) {
    return (
      <MainLayout>
        <div className="machine-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '18px' }}>{t('pages.monitoring.machine.loadingData')}</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 설비가 없는 경우
  if (!machinesLoading && allMachines.length === 0) {
    return (
      <MainLayout>
        <div className="machine-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '18px', marginBottom: '16px' }}>{t('pages.monitoring.machine.noEquipment')}</div>
            <div style={{ fontSize: '14px', opacity: 0.7 }}>{t('pages.monitoring.machine.registerEquipmentHint')}</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="machine-container">
        {/* 헤더 */}
        <div className="machine-header">
          <div className="machine-header-left">
            {/* 브레드크럼: 전사 › 모니터링 › 공장 › 사출기 선택 (Figma「개요」헤더) */}
            <nav className="machine-breadcrumb" aria-label="breadcrumb">
              <span className="material-symbols-outlined machine-breadcrumb-icon">factory</span>
              <button type="button" className="machine-breadcrumb-link" onClick={() => navigate('/enterprise')}>
                {t('pages.monitoring.machine.breadcrumbRoot')}
              </button>
              <span className="material-symbols-outlined machine-breadcrumb-sep">chevron_right</span>
              {/* 모니터링 드롭다운: 사이드바의 모니터링 화면으로 이동 */}
              <div className="machine-dropdown-wrapper machine-monitoring-dropdown">
                <button
                  type="button"
                  className="machine-dropdown-trigger"
                  aria-label={t('pages.monitoring.machine.selectMonitoringPage')}
                  onClick={() => { setIsMonitoringDropdownOpen(!isMonitoringDropdownOpen); setIsFactoryDropdownOpen(false); setIsDropdownOpen(false); }}
                >
                  <span className="machine-title">{t('pages.monitoring.machine.breadcrumbMonitoring')}</span>
                  <span className="material-symbols-outlined dropdown-icon">
                    {isMonitoringDropdownOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isMonitoringDropdownOpen && (
                  <div className="machine-dropdown-menu">
                    {MONITORING_PAGES.map((p) => (
                      <div
                        key={p.path}
                        className={`machine-dropdown-item ${location.pathname === p.path ? 'active' : ''}`}
                        onClick={() => { setIsMonitoringDropdownOpen(false); if (location.pathname !== p.path) navigate(p.path); }}
                      >
                        <div className="dropdown-item-name">{t(p.nameKey)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {factoryOptions.length > 0 && (
                <>
                  <span className="material-symbols-outlined machine-breadcrumb-sep">chevron_right</span>
                  {/* 공장 드롭다운: 사출기 드롭다운과 같은 모양. 고르면 그 공장의 첫 설비로 바뀌고
                      사출기 드롭다운에는 그 공장 설비만 보인다. 공장이 많아져도 목록으로 고를 수 있다. */}
                  <div className="machine-dropdown-wrapper machine-factory-dropdown">
                    <button
                      type="button"
                      className="machine-dropdown-trigger"
                      aria-label={t('pages.monitoring.machine.selectFactory')}
                      onClick={() => { setIsFactoryDropdownOpen(!isFactoryDropdownOpen); setIsDropdownOpen(false); }}
                    >
                      <span className="machine-title">
                        {factoryOptions.find((o) => o.key === activeFactoryKey)?.name || t('pages.monitoring.machine.selectFactory')}
                      </span>
                      <span className="material-symbols-outlined dropdown-icon">
                        {isFactoryDropdownOpen ? 'expand_less' : 'expand_more'}
                      </span>
                    </button>
                    {isFactoryDropdownOpen && (
                      <div className="machine-dropdown-menu">
                        {factoryOptions.map((opt) => (
                          <div
                            key={opt.key}
                            className={`machine-dropdown-item ${opt.key === activeFactoryKey ? 'active' : ''}`}
                            onClick={() => { selectFactory(opt); setIsFactoryDropdownOpen(false); }}
                          >
                            <div className="dropdown-item-name">{opt.name}</div>
                            <div className="dropdown-item-model">{t('pages.monitoring.machine.factoryMachineCount', { n: opt.machines.length })}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* 공장 사출 현황으로 이동. ID 는 응답의 factoryId 우선, 없으면 공장 목록에서 이름으로 찾는다 */}
                  <button
                    type="button"
                    className="machine-factory-link"
                    title={t('pages.monitoring.machine.factoryPage')}
                    aria-label={t('pages.monitoring.machine.factoryPage')}
                    onClick={() => {
                      const id = resolveFactoryId(currentMachine);
                      navigate(id ? `/factory?factoryId=${id}` : '/factory');
                    }}
                  >
                    <span className="material-symbols-outlined">open_in_new</span>
                  </button>
                </>
              )}
              <span className="material-symbols-outlined machine-breadcrumb-sep">chevron_right</span>
              <div className="machine-dropdown-wrapper">
                <button
                  className="machine-dropdown-trigger"
                  onClick={() => { setIsDropdownOpen(!isDropdownOpen); setIsFactoryDropdownOpen(false); }}
                >
                  <span className="machine-title">{currentMachine?.equipmentName || currentMachine?.name || t('pages.monitoring.machine.selectMachine')}</span>
                  <span className="material-symbols-outlined dropdown-icon">
                    {isDropdownOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isDropdownOpen && (
                  <div className="machine-dropdown-menu">
                    {visibleMachines.map((m) => (
                      <div
                        key={m.id}
                        className={`machine-dropdown-item ${selectedMachineId === m.id ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedMachineId(m.id);
                          setIsDropdownOpen(false);
                        }}
                      >
                        <div className="dropdown-item-name">{m.equipmentName}</div>
                        <div className="dropdown-item-model">{m.manufacturer} · {m.model}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </nav>
            <button
              className="machine-data-btn"
              onClick={() => window.open(`/machine-data?id=${selectedMachineId || machine?.id}`, '_blank', 'width=1800,height=900')}
              title="사출기 데이터 조회"
            >
              <span className="material-symbols-outlined">description</span>
            </button>
          </div>
          <div className="machine-header-right">
            <div ref={refreshLabelRef} className="update-time">
              {isOverviewLoading ? t('pages.monitoring.machine.loading') : (
                (() => {
                  // overview API 의 lastSyncTime(ETL 동기화 시각) 표시
                  const syncTime = overviewApiData?.lastSyncTime;
                  return formatSyncTime(syncTime || lastUpdated);
                })()
              )}
            </div>
            <button
              type="button"
              className="machine-refresh-btn"
              onClick={() => handleAutoRefresh()}
              title={t('pages.monitoring.machine.refresh')}
            >
              <span ref={refreshIconRef} className="material-symbols-outlined refresh-icon">refresh</span>
            </button>
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div className="machine-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              className={`tab-item ${activeTab === tab.name ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.name)}
              style={
                activeTab !== tab.name &&
                ['Overview', 'Cycle', '온도', '구동부', '사출설정', '알람', 'SPC', '감시1', '감시2', 'PPH'].includes(tab.name)
                  ? { backgroundColor: 'rgb(64, 41, 41)' }
                  : {}
              }
            >
              <span
                className="material-symbols-outlined tab-icon"
                style={{ color: tab.color }}
              >
                {tab.icon}
              </span>
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {/* 컨텐츠 영역 */}
        <div className="machine-content">
          {activeTab === '대시보드' && (
            <>
              {/* 위젯 그리드 · 툴바 · 편집 모드 · 위젯 추가/크기 선택 모달 — 새 디자인. 구현은 ./machine/WidgetDashboard 에 분리.
                  위젯 내용(renderWidget)과 사용자 모듈 빌더는 이 파일에 그대로 둔다 */}
              <WidgetDashboard
                equipmentId={selectedMachineId || machine?.id}
                widgets={dashboardWidgets}
                setWidgets={setDashboardWidgets}
                renderWidget={renderWidget}
                isModalOpen={isWidgetModalOpen}
                setIsModalOpen={setIsWidgetModalOpen}
                selectedSlot={selectedWidgetSlot}
                setSelectedSlot={setSelectedWidgetSlot}
                onOpenCustomBuilder={() => {
                  setSelectedWidgetType('사용자모듈');
                  setShowCustomWidgetBuilder(true);
                  setCustomWidgetConfig({
                    size: '1x1',
                    title: '',
                    chartType: 'line',
                    xAxis: 'shotNumber',
                    yAxis: 'pressure'
                  });
                }}
              />

              {/* 사용자 모듈 빌더 */}
              {showCustomWidgetBuilder && (
                <div className="widget-modal-overlay" onClick={() => {
                  setShowCustomWidgetBuilder(false);
                  setSelectedWidgetType(null);
                }}>
                  <div className="custom-widget-builder" onClick={(e) => e.stopPropagation()}>
                    <div className="builder-header">
                      <h2>{t('pages.monitoring.machine.widget.builder.title')}</h2>
                      <button className="modal-close-btn" onClick={() => {
                        setShowCustomWidgetBuilder(false);
                        setSelectedWidgetType(null);
                      }}>
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                    <div className="builder-body">
                      {/* 좌측 설정 영역 */}
                      <div className="builder-settings">
                        <div className="setting-group">
                          <label className="setting-label">{t('pages.monitoring.machine.widget.builder.widgetSize')}</label>
                          <div className="size-button-group">
                            {['1x1', '2x1', '1x2', '2x2'].map(size => (
                              <button
                                key={size}
                                className={`size-btn ${customWidgetConfig.size === size ? 'active' : ''}`}
                                onClick={() => setCustomWidgetConfig({...customWidgetConfig, size})}
                              >
                                {size === '1x1' && t('pages.monitoring.machine.widget.modal.size1x1')}
                                {size === '2x1' && t('pages.monitoring.machine.widget.modal.size2x1')}
                                {size === '1x2' && t('pages.monitoring.machine.widget.modal.size1x2')}
                                {size === '2x2' && t('pages.monitoring.machine.widget.modal.size2x2')}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">{t('pages.monitoring.machine.widget.builder.widgetTitle')}</label>
                          <input
                            type="text"
                            className="setting-input"
                            placeholder={t('pages.monitoring.machine.widget.builder.widgetTitlePlaceholder')}
                            value={customWidgetConfig.title}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, title: e.target.value})}
                          />
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">{t('pages.monitoring.machine.widget.builder.chartType')}</label>
                          <select
                            className="setting-select"
                            value={customWidgetConfig.chartType}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, chartType: e.target.value})}
                          >
                            <option value="line">{t('pages.monitoring.machine.widget.builder.chartLine')}</option>
                            <option value="bar">{t('pages.monitoring.machine.widget.builder.chartBar')}</option>
                            <option value="area">{t('pages.monitoring.machine.widget.builder.chartArea')}</option>
                          </select>
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">{t('pages.monitoring.machine.widget.builder.xAxis')}</label>
                          <select
                            className="setting-select"
                            value={customWidgetConfig.xAxis}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, xAxis: e.target.value})}
                          >
                            <option value="shotNumber">{t('pages.monitoring.machine.widget.builder.xShotNumber')}</option>
                            <option value="time">{t('pages.monitoring.machine.widget.builder.xTime')}</option>
                          </select>
                        </div>

                        <div className="setting-group">
                          <label className="setting-label">{t('pages.monitoring.machine.widget.builder.yAxis')}</label>
                          <select
                            className="setting-select"
                            value={customWidgetConfig.yAxis}
                            onChange={(e) => setCustomWidgetConfig({...customWidgetConfig, yAxis: e.target.value})}
                          >
                            <option value="pressure">{t('pages.monitoring.machine.widget.builder.yPressure')}</option>
                            <option value="temperature">{t('pages.monitoring.machine.widget.builder.yTemperature')}</option>
                            <option value="speed">{t('pages.monitoring.machine.widget.builder.ySpeed')}</option>
                            <option value="position">{t('pages.monitoring.machine.widget.builder.yPosition')}</option>
                          </select>
                        </div>
                      </div>

                      {/* 우측 미리보기 및 설정 요약 */}
                      <div className="builder-preview">
                        <div className="preview-section">
                          <h3 className="preview-title">{t('pages.monitoring.machine.widget.builder.preview')}</h3>
                          <div className="preview-chart">
                            {renderCustomPreview()}
                          </div>
                        </div>

                        <div className="summary-section">
                          <h3 className="summary-title">{t('pages.monitoring.machine.widget.builder.summary')}</h3>
                          <div className="summary-list">
                            <div className="summary-item">
                              <span className="summary-key">{t('pages.monitoring.machine.widget.builder.summarySize')}</span>
                              <span className="summary-value">{customWidgetConfig.size}</span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">{t('pages.monitoring.machine.widget.builder.summaryTitle')}</span>
                              <span className="summary-value">{customWidgetConfig.title || t('pages.monitoring.machine.widget.builder.summaryTitleEmpty')}</span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">{t('pages.monitoring.machine.widget.builder.summaryChart')}</span>
                              <span className="summary-value">
                                {customWidgetConfig.chartType === 'line' && t('pages.monitoring.machine.widget.builder.chartLine')}
                                {customWidgetConfig.chartType === 'bar' && t('pages.monitoring.machine.widget.builder.chartBar')}
                                {customWidgetConfig.chartType === 'area' && t('pages.monitoring.machine.widget.builder.chartArea')}
                              </span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">{t('pages.monitoring.machine.widget.builder.summaryX')}</span>
                              <span className="summary-value">
                                {customWidgetConfig.xAxis === 'shotNumber' && t('pages.monitoring.machine.widget.builder.xShotNumber')}
                                {customWidgetConfig.xAxis === 'time' && t('pages.monitoring.machine.widget.builder.xTime')}
                              </span>
                            </div>
                            <div className="summary-item">
                              <span className="summary-key">{t('pages.monitoring.machine.widget.builder.summaryY')}</span>
                              <span className="summary-value">
                                {customWidgetConfig.yAxis === 'pressure' && t('pages.monitoring.machine.widget.builder.yPressure')}
                                {customWidgetConfig.yAxis === 'temperature' && t('pages.monitoring.machine.widget.builder.yTemperature')}
                                {customWidgetConfig.yAxis === 'speed' && t('pages.monitoring.machine.widget.builder.ySpeed')}
                                {customWidgetConfig.yAxis === 'position' && t('pages.monitoring.machine.widget.builder.yPosition')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="builder-footer">
                      <button
                        className="builder-cancel-btn"
                        onClick={() => {
                          setShowCustomWidgetBuilder(false);
                          setSelectedWidgetType(null);
                        }}
                      >
                        {t('pages.monitoring.machine.widget.builder.cancel')}
                      </button>
                      <button
                        className="builder-complete-btn"
                        onClick={() => {
                          setDashboardWidgets([...dashboardWidgets, {
                            id: Date.now(),
                            type: '사용자모듈',
                            size: customWidgetConfig.size,
                            slot: selectedWidgetSlot,
                            config: {
                              title: customWidgetConfig.title,
                              chartType: customWidgetConfig.chartType,
                              xAxis: customWidgetConfig.xAxis,
                              yAxis: customWidgetConfig.yAxis
                            }
                          }]);
                          setShowCustomWidgetBuilder(false);
                          setIsWidgetModalOpen(false);
                          setSelectedWidgetType(null);
                        }}
                      >
                        {t('pages.monitoring.machine.widget.builder.complete')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {/* 개요 — 새 디자인(Figma 개요) 탭. 구현은 ./machine/OverviewNew 에 분리 */}
          {activeTab === '개요' && (
            <OverviewNew
              equipmentId={selectedMachineId || machine?.id}
              factoryId={resolveFactoryId(currentMachine)}
              onNavigateTab={navigateTab}
            />
          )}

          {/* 공정 — 새 디자인(Figma 공정: 사이클/온도/구동/감시/설정값) 탭. 구현은 ./machine/ProcessNew 에 분리 */}
          {activeTab === '공정' && (
            <ProcessNew
              equipmentId={selectedMachineId || machine?.id}
              initialSubTab={subTabRequest['공정'] || 'cycle'}
              onAddWidget={addInjectionInfoWidget}
            />
          )}

          {/* 품질 · 생산 — 새 디자인 탭(서브탭: 공정관리 SPC / 공정검사 / 비전검사 / 생산). 구현은 ./machine/InspectionPhotos 에 분리 */}
          {activeTab === '검수사진' && (
            <InspectionPhotos
              equipmentId={selectedMachineId || machine?.id}
              machine={currentMachine}
              onNavigateTab={navigateTab}
              initialSubTab={subTabRequest['검수사진'] || 'spc'}
            />
          )}

          {/* 데이터 · 로그 — 새 디자인(Figma 데이터·로그) 탭. 구현은 ./machine/DataLog 에 분리 */}
          {activeTab === '데이터로그' && (
            <DataLog
              equipmentId={selectedMachineId || machine?.id}
              initialSubTab={subTabRequest['데이터로그'] || 'shots'}
              factories={factoryList}
              machines={allMachines}
              currentMachine={currentMachine}
              onSelectMachine={(m) => setSelectedMachineId(m.id)}
            />
          )}

          {!['대시보드', '개요', '공정', '검수사진', '데이터로그'].includes(activeTab) && (
            <div style={{ padding: '20px', color: '#9CA3AF' }}>
              {t('pages.monitoring.machine.tabPlaceholder', { tab: activeTab })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}

export default Machine;
